import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import DispatchService from './DispatchService';
import OrderByOptionService from '../orderByOption/OrderByOptionService';
import WeeklyHoursDialog from './WeeklyHoursDialog';
import ShiftsDialog from './ShiftsDialog';
import DailyWindowDialog, { windowSummary } from './DailyWindowDialog';
import { feeSummary } from '../orderByOption/feeTiers';
import { weeklySummary, shiftsSummary, NO_SHIFTS } from './weeklyHours';

// Mercari dispatch bot settings: timers and each buyer (คนกด). English on purpose: the owner uses it.
// Backend: /api/dispatch/settings and /api/dispatch/buyers (backend DISPATCH_BOT.md).

const TIMERS = [
  { field: 'offerMinutes', label: 'Card answer time', help: 'No answer by then → next buyer' },
  { field: 'lastOfferMinutes', label: 'Last buyer', help: 'The last buyer in the queue gets longer, then admin buys it' },
  { field: 'pinnedReminderMinutes', label: 'Negotiated item reminder', help: 'Chosen buyer has not answered → remind admin' },
  { field: 'claimNoProofMinutes', label: 'Sold: wait for proof', help: 'Mercari says sold while a buyer had it open → wait this long for their proof, then ask them' },
  { field: 'burstMaxCards', label: 'Burst: max cards', help: 'This many cards to one buyer within the window → skip them', max: 50 },
  { field: 'burstWindowMinutes', label: 'Burst window (minutes)', help: 'Counted back from now' },
  { field: 'shopOfferMinutes', label: 'Shops item: card time', help: 'Mercari Shops (may have stock): time to open the card; then it CLOSES and the next buyer gets one' },
  { field: 'shopEngagedMinutes', label: 'Shops item: after opening', help: 'Opened the item link → this long (once) to buy + attach proof; nobody else gets a card; then admin chases' },
];

const NO_TRACKING_OPTIONS = [
  { value: 'first', label: 'First' },
  { value: 'normal', label: 'Normal' },
  { value: 'never', label: 'Never' },
];

const SWITCHES = [
  { field: 'dispatchEnabled', label: 'Enabled' },
  { field: 'dispatchSea', label: 'Sea' },
  { field: 'dispatchAir', label: 'Air' },
  { field: 'dispatchSmile', label: 'Smile' },
  { field: 'dispatchTama', label: 'Tama' },
  { field: 'dispatchAcceptsBig', label: 'Big items' },
];

// Direct / fallback buyer (FujiKing) without a LINE group: gets admin tasks, never a card.
const isNoCard = (b) => !b.lineGroupId && !b.dispatchLineId && (b.dispatchDirectFromJpy != null || Boolean(b.dispatchFallback));

const NotUsed = () => <Typography variant="caption" color="text.secondary">Not used (no card)</Typography>;

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

const thaiTime = (iso) => new Date(iso).toLocaleTimeString('en-GB', {
  hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok',
});

/** "On now" chip, plus today's manual change while it lasts. */
const OnlineChip = ({ buyer }) => {
  const active = buyer.dispatchOverride && buyer.dispatchOverrideUntil
    && new Date(buyer.dispatchOverrideUntil) > new Date();
  if (isNoCard(buyer)) {
    const on = buyer.dispatchEnabled && buyer.isActive !== false && !buyer.isSuspended;
    return <Chip size="small" color={on ? 'info' : 'default'} label={on ? 'Always (no card)' : 'Off'} />;
  }
  return (
    <Box>
      <Chip
        size="small"
        color={buyer.onlineNow ? 'success' : 'default'}
        label={buyer.onlineNow ? 'On' : 'Off'}
      />
      {active && (
        <Typography variant="caption" display="block" color="text.secondary">
          {buyer.dispatchOverride === 'on' ? 'Opened' : 'Closed'} until {thaiTime(buyer.dispatchOverrideUntil)}
        </Typography>
      )}
    </Box>
  );
};

export default function DispatchSetting() {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [timers, setTimers] = useState(null);
  const [savingTimers, setSavingTimers] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [priorities, setPriorities] = useState({}); // id → text being typed
  const [directs, setDirects] = useState({}); // id → "Direct from ¥" text being typed
  const [orderByRows, setOrderByRows] = useState([]); // active Order By rows, for "Add a no-card buyer"
  const [hoursFor, setHoursFor] = useState(null); // buyer whose hours dialog is open
  const [shiftsFor, setShiftsFor] = useState(null); // shift buyer whose shifts dialog is open
  const [windowFor, setWindowFor] = useState(null); // buyer whose daily-window dialog is open

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, settingsRes, buyersRes, orderByRes] = await Promise.all([
        DispatchService.getStatus(),
        DispatchService.getSettings(),
        DispatchService.getBuyers(),
        OrderByOptionService.getActiveOrderByOptions(),
      ]);
      setOrderByRows(orderByRes.data || []);
      setStatus(statusRes.data);
      setTimers(settingsRes.data.timers);
      setBuyers(buyersRes.data.buyers);
      setPriorities({});
      setDirects({});
    } catch (err) {
      toast.error(errorText(err, 'Could not load the settings'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const replaceBuyer = (updated) => {
    setBuyers((prev) => prev.map((b) => (b.id === updated.id ? { ...b, ...updated } : b)));
  };

  const saveTimers = async () => {
    try {
      setSavingTimers(true);
      const res = await DispatchService.updateSettings(timers);
      setTimers(res.data.timers);
      toast.success('Timers saved');
    } catch (err) {
      toast.error(errorText(err, 'Could not save the timers'));
    } finally {
      setSavingTimers(false);
    }
  };

  const saveBuyer = async (id, data) => {
    try {
      setBusyId(id);
      const res = await DispatchService.updateBuyer(id, data);
      replaceBuyer(res.data.buyer);
      return true;
    } catch (err) {
      toast.error(errorText(err, 'Could not save'));
      return false;
    } finally {
      setBusyId(null);
    }
  };

  const savePriority = async (buyer) => {
    const text = priorities[buyer.id];
    if (text === undefined || Number(text) === buyer.dispatchPriority) return;
    if (await saveBuyer(buyer.id, { dispatchPriority: Number(text) })) {
      setPriorities((prev) => {
        const next = { ...prev };
        delete next[buyer.id];
        return next;
      });
    }
  };

  const saveDirect = async (buyer) => {
    const text = directs[buyer.id];
    if (text === undefined) return;
    const value = text.trim() === '' ? null : Number(text); // empty = never direct
    if (value === buyer.dispatchDirectFromJpy) return;
    if (await saveBuyer(buyer.id, { dispatchDirectFromJpy: value })) {
      setDirects((prev) => {
        const next = { ...prev };
        delete next[buyer.id];
        return next;
      });
    }
  };

  const testCard = async (buyer) => {
    if (!window.confirm(`Send one test card to ${buyer.label}'s LINE (${buyer.dispatchLineId})? This is a real LINE message.`)) return;
    try {
      setBusyId(buyer.id);
      await DispatchService.sendTestCard(buyer.id);
      toast.success(`Test card sent to ${buyer.label}`);
    } catch (err) {
      toast.error(errorText(err, 'Could not send the test card'));
    } finally {
      setBusyId(null);
    }
  };

  // First setup of a FujiKing-type buyer: it is not listed until direct / fallback is set.
  const addNoCardBuyer = async (id) => {
    const row = orderByRows.find((o) => o.id === id);
    if (!row || !window.confirm(`Add ${row.label} as a no-card buyer (Fallback on)? Then set Sea / Air, Direct from ¥ and Enabled on its row.`)) return;
    if (await saveBuyer(id, { dispatchFallback: true })) {
      const buyersRes = await DispatchService.getBuyers();
      setBuyers(buyersRes.data.buyers);
      toast.success(`${row.label} added`);
    }
  };

  const today = async (buyer, open) => {
    try {
      setBusyId(buyer.id);
      await (open ? DispatchService.openToday(buyer.id) : DispatchService.closeToday(buyer.id));
      const buyersRes = await DispatchService.getBuyers(); // onlineNow is worked out by the backend
      setBuyers(buyersRes.data.buyers);
      const b = buyersRes.data.buyers.find((x) => x.id === buyer.id);
      const until = b?.dispatchOverrideUntil && new Date(b.dispatchOverrideUntil) > new Date()
        ? ` until ${thaiTime(b.dispatchOverrideUntil)}` : '';
      toast.info(`${buyer.label}: ${b?.onlineNow ? 'on' : 'off'}${until}`);
    } catch (err) {
      toast.error(errorText(err, 'Could not change it'));
    } finally {
      setBusyId(null);
    }
  };

  const saveHours = async (weekly) => {
    if (await saveBuyer(hoursFor.id, { dispatchWeeklyHours: weekly })) {
      toast.success(`Hours saved for ${hoursFor.label}`);
      setHoursFor(null);
    }
  };

  const saveShifts = async (shifts) => {
    if (await saveBuyer(shiftsFor.id, { dispatchShifts: shifts })) {
      toast.success(`Shifts saved for ${shiftsFor.label}`);
      setShiftsFor(null);
    }
  };

  const saveWindow = async (win) => {
    if (await saveBuyer(windowFor.id, { dispatchDailyWindow: win })) {
      toast.success(win ? `Daily window saved for ${windowFor.label}` : `Daily window removed for ${windowFor.label}`);
      setWindowFor(null);
    }
  };

  const copyLink = async (token) => {
    const url = DispatchService.buyerLinkUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied — send it to the buyer in LINE');
    } catch {
      window.prompt('Copy this link', url); // clipboard blocked → let the admin copy it by hand
    }
  };

  const newLink = async (buyer) => {
    if (buyer.dispatchAccessToken
      && !window.confirm(`Make a new link for ${buyer.label}? The old link stops working.`)) return;
    try {
      setBusyId(buyer.id);
      const res = await DispatchService.makeLink(buyer.id);
      replaceBuyer({ id: buyer.id, dispatchAccessToken: res.data.token });
      await copyLink(res.data.token);
    } catch (err) {
      toast.error(errorText(err, 'Could not make the link'));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: 3, display: 'grid', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Typography variant="h5">Mercari Dispatch Bot</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>Refresh</Button>
      </Box>

      {status && !status.enabled && (
        <Alert severity="info">
          The bot is off — no cards are sent.{status.missing?.length ? ` Missing: ${status.missing.join(', ')}` : ''}
        </Alert>
      )}

      {timers && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Timers (minutes) and burst limit</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
            {TIMERS.map(({ field, label, help, max = 1440 }) => (
              <TextField
                key={field}
                id={`timer-${field}`}
                type="number"
                size="small"
                label={label}
                helperText={help}
                value={timers[field] ?? ''}
                inputProps={{ min: 1, max, step: 1 }}
                onChange={(e) => setTimers((prev) => ({ ...prev, [field]: e.target.value === '' ? '' : Number(e.target.value) }))}
              />
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={saveTimers} disabled={savingTimers}>
              Save timers
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">Buyers</Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Buyers with a Buyer LINE or a LINE group, plus FujiKing-type buyers (direct / fallback: admin orders it, no card) ·
          lower priority number gets the card first; a tie goes to whoever has
          fewer jobs today · hours: press Edit (not set = no cards)
        </Typography>
        <Select
          size="small"
          displayEmpty
          value=""
          sx={{ mb: 1, minWidth: 260 }}
          onChange={(e) => addNoCardBuyer(e.target.value)}
        >
          <MenuItem value="" disabled>Add a no-card buyer (FujiKing)…</MenuItem>
          {orderByRows.filter((o) => !buyers.some((b) => b.id === o.id)).map((o) => (
            <MenuItem key={o.id} value={o.id}>{o.label}</MenuItem>
          ))}
        </Select>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Now</TableCell>
                <TableCell>Buyer LINE</TableCell>
                {SWITCHES.map((s) => <TableCell key={s.field} align="center">{s.label}</TableCell>)}
                <TableCell>No tracking</TableCell>
                <TableCell>Priority (low first)</TableCell>
                <TableCell>No card (FujiKing)</TableCell>
                <TableCell>Fee (Order By)</TableCell>
                <TableCell>Hours</TableCell>
                <TableCell>Daily window</TableCell>
                <TableCell>Today</TableCell>
                <TableCell>Buyer's link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buyers.map((b) => {
                const busy = busyId === b.id;
                return (
                  <TableRow key={b.id}>
                    <TableCell>{b.label}</TableCell>
                    <TableCell><OnlineChip buyer={b} /></TableCell>
                    <TableCell>
                      {isNoCard(b) ? <NotUsed /> : (
                        <Typography
                          variant="body2"
                          component="span"
                          sx={{ fontFamily: 'monospace', mr: 1 }}
                          title="Buyer's LINE (Chatcone socialId on the Tamachan LINE) — cards go there 1-to-1. Read-only here: change it on the Order By page, which asks before saving (owner 2026-11-01: a LINE ID changed by accident and the buyer's cards stopped)."
                        >
                          {b.dispatchLineId || (b.lineGroupId ? 'LINE group' : '—')}
                        </Typography>
                      )}
                      {b.dispatchLineId && !isNoCard(b) && (
                        <Button size="small" disabled={busy} onClick={() => testCard(b)}>Test card</Button>
                      )}
                    </TableCell>
                    {SWITCHES.map((s) => (
                      <TableCell key={s.field} align="center">
                        <Switch
                          size="small"
                          checked={Boolean(b[s.field])}
                          disabled={busy}
                          onChange={(e) => saveBuyer(b.id, { [s.field]: e.target.checked })}
                        />
                      </TableCell>
                    ))}
                    <TableCell>
                      <Select
                        size="small"
                        value={b.dispatchNoTracking}
                        disabled={busy}
                        onChange={(e) => saveBuyer(b.id, { dispatchNoTracking: e.target.value })}
                      >
                        {NO_TRACKING_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        id={`priority-${b.id}`}
                        type="number"
                        size="small"
                        sx={{ width: 90 }}
                        value={priorities[b.id] ?? b.dispatchPriority}
                        disabled={busy}
                        inputProps={{ min: 0, step: 1 }}
                        onChange={(e) => setPriorities((prev) => ({ ...prev, [b.id]: e.target.value }))}
                        onBlur={() => savePriority(b)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <TextField
                        id={`direct-${b.id}`}
                        type="number"
                        size="small"
                        label="Direct from ¥"
                        placeholder="never"
                        sx={{ width: 140 }}
                        value={directs[b.id] ?? (b.dispatchDirectFromJpy ?? '')}
                        disabled={busy}
                        inputProps={{ min: 1, step: 1 }}
                        onChange={(e) => setDirects((prev) => ({ ...prev, [b.id]: e.target.value }))}
                        onBlur={() => saveDirect(b)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                      />
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Switch
                          size="small"
                          checked={Boolean(b.dispatchFallback)}
                          disabled={busy}
                          onChange={(e) => saveBuyer(b.id, { dispatchFallback: e.target.checked })}
                        />
                        <Typography variant="body2">Fallback</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }} title="Edit the fee on Setting → Order By">
                      {(() => { const f = feeSummary(b); return f.lines.map((line) => (
                        <Typography key={line} variant="body2" color={f.old ? 'warning.main' : 'text.primary'} sx={{ whiteSpace: 'nowrap' }}>{line}</Typography>
                      )); })()}
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      {isNoCard(b) ? <NotUsed /> : (<>
                      <Select
                        size="small"
                        value={b.dispatchScheduleType}
                        disabled={busy}
                        onChange={(e) => saveBuyer(b.id, { dispatchScheduleType: e.target.value })}
                        sx={{ mb: 0.5 }}
                      >
                        <MenuItem value="weekly">Weekly</MenuItem>
                        <MenuItem value="shift">Shift (buyer sets)</MenuItem>
                      </Select>
                      {b.dispatchScheduleType === 'shift' ? (
                        <>
                          <Typography variant="body2" color={shiftsSummary(b.dispatchShifts) === NO_SHIFTS ? 'warning.main' : 'text.primary'}>
                            {shiftsSummary(b.dispatchShifts)}
                          </Typography>
                          <Button size="small" disabled={busy} onClick={() => setShiftsFor(b)}>Edit shifts</Button>
                        </>
                      ) : (
                        <>
                          <Typography variant="body2" color={b.dispatchWeeklyHours ? 'text.primary' : 'warning.main'}>
                            {weeklySummary(b.dispatchWeeklyHours)}
                          </Typography>
                          <Button size="small" disabled={busy} onClick={() => setHoursFor(b)}>Edit</Button>
                        </>
                      )}
                      </>)}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }} title="Admin-only, every day until removed. The buyer is not told.">
                      {isNoCard(b) ? <NotUsed /> : (<>
                        {b.dispatchDailyWindow ? (
                          <Typography variant="body2" color={b.activeWindow ? (b.activeWindow.mode === 'off' ? 'error.main' : 'success.main') : 'text.primary'}>
                            {windowSummary(b.dispatchDailyWindow)}{b.activeWindow ? ' · now' : ''}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        )}
                        <Button size="small" disabled={busy} onClick={() => setWindowFor(b)}>{b.dispatchDailyWindow ? 'Edit' : 'Add'}</Button>
                      </>)}
                    </TableCell>
                    <TableCell>
                      {isNoCard(b) ? <NotUsed /> : b.dispatchEnabled ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" variant="outlined" color="success" disabled={busy} onClick={() => today(b, true)}>
                            Open
                          </Button>
                          <Button size="small" variant="outlined" color="inherit" disabled={busy} onClick={() => today(b, false)}>
                            Close
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Turn on "Enabled" first</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isNoCard(b) ? <NotUsed /> : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {b.dispatchAccessToken && (
                          <Button size="small" variant="outlined" disabled={busy} onClick={() => copyLink(b.dispatchAccessToken)}>
                            Copy
                          </Button>
                        )}
                        <Button size="small" variant="text" disabled={busy} onClick={() => newLink(b)}>
                          {b.dispatchAccessToken ? 'New link' : 'Make link'}
                        </Button>
                      </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {buyers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={15} align="center">No buyer has a Buyer LINE or LINE group yet</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      <WeeklyHoursDialog buyer={hoursFor} onClose={() => setHoursFor(null)} onSave={saveHours} />
      <ShiftsDialog buyer={shiftsFor} onClose={() => setShiftsFor(null)} onSave={saveShifts} />
      <DailyWindowDialog buyer={windowFor} onClose={() => setWindowFor(null)} onSave={saveWindow} />
    </Box>
  );
}
